import moengage from '@moengage/web-sdk';

import { logtoCookies } from './cookies';
import { getRuntimeConfig } from './config';
import { normalizeAppName } from './app-name';

const LOG_PREFIX = '[MoEngage UserAttr]';

// ------------------------------------------------------------------
// Internal helpers
// ------------------------------------------------------------------

/**
 * Normalizes phone number to standard format: 66625400450
 * Handles SmartInputField quirk where countryCode + localInput = "660625400450"
 *
 * Input → Output:
 *   660625400450  → 66625400450  (strip leading 0 after country code 66)
 *   0625400450    → 66625400450  (convert local Thai format)
 *   +66625400450  → 66625400450  (strip +)
 *   66625400450   → 66625400450  (already correct)
 *   12025551234   → 12025551234  (non-Thai, untouched)
 */
const normalizePhone = (phone: string): string => {
  let normalized = phone;
  // Strip + prefix
  if (normalized.startsWith('+')) {
    normalized = normalized.slice(1);
  }
  // Strip leading 0 after Thai country code: 660625400450 → 66625400450
  if (normalized.startsWith('66') && normalized.charAt(2) === '0') {
    normalized = '66' + normalized.slice(3);
  }
  // Convert local Thai format: 0625400450 → 66625400450
  if (normalized.startsWith('0')) {
    normalized = '66' + normalized.slice(1);
  }
  return normalized;
};

/**
 * Fetches the application name from the One Member API
 */
const fetchApplicationName = async (appId: string): Promise<string | undefined> => {
  try {
    const config = getRuntimeConfig();
    const url = `${config.VITE_ONE_MEMBER_API_ENDPOINT}/api/applications/${appId}`;

    const response = await fetch(url);

    if (!response.ok) {
      console.error(`${LOG_PREFIX} Failed to fetch application name:`, response.statusText);
      return undefined;
    }

    const data = await response.json();

    if (data && typeof data === 'object' && 'name' in data && typeof data.name === 'string') {
      return data.name;
    }

    return undefined;
  } catch (error) {
    console.error(`${LOG_PREFIX} Error fetching application name:`, error);
    return undefined;
  }
};

type LookupResult = {
  userId?: string;
  primaryEmail?: string | null;
  primaryPhone?: string | null;
  name?: string | null;
  avatar?: string | null;
};

/**
 * Looks up the user via One Member API's public endpoint.
 * GET /api/users/lookup?email=xxx or ?phone=xxx or ?connectorId=google
 * Returns userId + basic profile data from Logto (email, phone, name, avatar)
 */
const lookupUser = async (
  identifier?: { type: 'email' | 'phone'; value: string },
  socialConnectorId?: string
): Promise<LookupResult | undefined> => {
  try {
    const config = getRuntimeConfig();
    const params = new URLSearchParams();

    if (identifier) {
      const value = identifier.type === 'phone' ? normalizePhone(identifier.value) : identifier.value;
      params.set(identifier.type, value);
    }
    if (socialConnectorId) {
      params.set('connectorId', socialConnectorId);
    }

    if (params.size === 0) {
      return undefined;
    }

    const url = `${config.VITE_ONE_MEMBER_API_ENDPOINT}/api/users/lookup?${params.toString()}`;
    const response = await fetch(url);

    if (!response.ok) {
      console.error(`${LOG_PREFIX} User lookup failed: ${response.status}`);
      return undefined;
    }

    return (await response.json()) as LookupResult;
  } catch (error) {
    console.error(`${LOG_PREFIX} Error looking up user:`, error);
    return undefined;
  }
};

// ------------------------------------------------------------------
// Public API
// ------------------------------------------------------------------

/**
 * Sets the signupFromApp MoEngage user attribute directly in experience-ui.
 * This should be called ONCE when a user successfully registers.
 *
 * @returns The normalized app name (VIPA | KIDS | ALTV | ThaiPBS) or undefined if not resolved
 */
export const setSignupFromApp = async (): Promise<string | undefined> => {
  const { appId } = logtoCookies;

  if (!appId) {
    return undefined;
  }

  let normalizedAppName: string | undefined;

  try {
    const applicationName = await fetchApplicationName(appId);

    if (!applicationName) {
      return undefined;
    }

    normalizedAppName = normalizeAppName(applicationName);
  } catch (error) {
    console.error(`${LOG_PREFIX} Error fetching app name:`, error);
    return undefined;
  }

  if (!normalizedAppName) {
    return undefined;
  }

  // MoEngage SDK call — best-effort, must never lose normalizedAppName
  try {
    moengage.add_user_attribute('signupFromApp', normalizedAppName);
    console.log(`${LOG_PREFIX} signupFromApp="${normalizedAppName}"`);
  } catch (error) {
    console.error(`${LOG_PREFIX} MoEngage add_user_attribute failed (signupFromApp still returned):`, error);
  }

  return normalizedAppName;
};

/**
 * Looks up the userId via One Member API, identifies the user in MoEngage,
 * and syncs all available profile attributes from form values + identifier.
 *
 * This mirrors what edit-profile-client does in `initializeMoEngageUser()`:
 *   1. moengage.identifyUser(userId)
 *   2. Built-in attributes: first_name, last_name, email, mobile, gender, birthday
 *   3. Custom attributes: name, birthMonth, birthYear, interests, province, etc.
 *
 * @param formValues  - Extra-profile form values submitted during registration
 * @param identifier  - The email or phone used to register (required for userId lookup)
 * @returns The userId if successfully resolved, undefined otherwise
 */
export type UserSyncResult = {
  userId: string;
  userAttributes: Record<string, unknown>;
};

export const identifyAndSyncUser = async (
  formValues: Record<string, unknown> = {},
  identifier?: { type: 'email' | 'phone'; value: string },
  socialConnectorId?: string
): Promise<UserSyncResult | undefined> => {
  // 1. Look up user via One Member API (by email/phone or by social connector)
  //    This is separated from MoEngage calls so userId is never lost if the SDK throws.
  let lookupResult: LookupResult | undefined;
  let userId: string | undefined;

  try {
    lookupResult = await lookupUser(identifier, socialConnectorId);
    userId = lookupResult?.userId;
  } catch (error) {
    console.error(`${LOG_PREFIX} User lookup failed:`, error);
  }

  if (!userId) {
    console.warn(`${LOG_PREFIX} identifyAndSyncUser: could not resolve userId`);
    return undefined;
  }

  // 2. Resolve user attributes from form values + identifier + lookup result
  //    These are computed once and used for both MoEngage SDK (best-effort) and sendBeacon (guaranteed).
  const givenName = formValues.givenName as string | undefined;
  const familyName = formValues.familyName as string | undefined;
  const gender = formValues.gender as string | undefined;
  const nickname = formValues.nickname as string | undefined;
  const birthMonth = formValues.birthMonth as string | undefined;
  const birthYear = formValues.birthYear as string | undefined;
  const name = (formValues.name as string | undefined) ?? lookupResult?.name ?? undefined;
  // Social registrations have no givenName (the form hides the name field), so the
  // full name from the lookup goes into MoEngage's standard First Name instead —
  // otherwise the profile shows "Guest" forever.
  const firstName = givenName ?? name;
  const interests = formValues.interests as string[] | undefined;
  const province = formValues.province as string | undefined;
  const address = formValues.address as
    | { postalCode?: string; region?: string; locality?: string }
    | undefined;

  const email = (identifier?.type === 'email' && identifier.value)
    ? identifier.value
    : lookupResult?.primaryEmail ?? undefined;

  const phone = (identifier?.type === 'phone' && identifier.value)
    ? normalizePhone(identifier.value)
    : lookupResult?.primaryPhone ? normalizePhone(lookupResult.primaryPhone) : undefined;

  // Build userAttributes for sendBeacon (matches what MoEngage SDK would set on web/iOS)
  const userAttributes: Record<string, unknown> = {};
  if (email) userAttributes.email = email;
  if (phone) userAttributes.mobile = phone;
  if (firstName) userAttributes.first_name = firstName;
  if (familyName) userAttributes.last_name = familyName;
  if (gender) userAttributes.gender = gender;
  if (name) userAttributes.name = name;
  if (nickname) userAttributes.nickname = nickname;
  if (birthMonth) userAttributes.birthMonth = birthMonth;
  if (birthYear) userAttributes.birthYear = birthYear;
  if (lookupResult?.avatar) userAttributes.avatar = lookupResult.avatar;
  if (interests !== undefined) {
    userAttributes.interests = interests.length === 0 ? 'Unknown' : interests;
  }
  if (province) userAttributes.province = province;
  if (address) {
    if (address.postalCode) userAttributes.postalCode = address.postalCode;
    if (address.region) userAttributes.region = address.region;
    if (address.locality) userAttributes.locality = address.locality;
    userAttributes.address = address;
  }
  if (birthMonth && birthYear) {
    const month = parseInt(birthMonth, 10);
    const year = parseInt(birthYear, 10);
    if (!isNaN(month) && month >= 1 && month <= 12 && !isNaN(year) && year >= 1900) {
      userAttributes.birthday = new Date(year, month - 1, 1).toISOString();
    }
  }

  // Email subscription flags (isSubscribeDailyDigest, isSubscribeCatchUp, ...)
  // are dynamic keys from the extra-profile form; pass them all through
  const subscriptionFlags = Object.entries(formValues).filter(
    ([key, val]) => key.startsWith('isSubscribe') && typeof val === 'boolean'
  ) as Array<[string, boolean]>;
  for (const [key, val] of subscriptionFlags) {
    userAttributes[key] = val;
  }

  // 3. MoEngage SDK calls — best-effort (works on web/iOS, may fail on Android)
  try {
    // GetUserId throws "Cannot read properties of null" while the SDK is still
    // initializing its store — never let that kill identifyUser + attribute sync below
    const existingUserId = (() => {
      try {
        return moengage.getUserId();
      } catch {
        return '';
      }
    })();
    if (existingUserId && existingUserId !== '' && existingUserId !== userId) {
      console.log(`${LOG_PREFIX} destroying previous session: "${existingUserId}" → "${userId}"`);
      // MUST complete before identifyUser — racing them lets the SDK carry the
      // previous user's cached attributes (e.g. email) onto the new identity
      await moengage.destroy_session();
    }

    console.log(`${LOG_PREFIX} identifyUser: id="${userId}"`);
    await moengage.identifyUser(userId);

    if (firstName) moengage.add_first_name(firstName);
    if (familyName) moengage.add_last_name(familyName);
    if (gender) moengage.add_gender(gender);
    if (email) moengage.add_email(email);
    if (phone) moengage.add_mobile(phone);
    if (birthMonth && birthYear) {
      const month = parseInt(birthMonth, 10);
      const year = parseInt(birthYear, 10);
      if (!isNaN(month) && month >= 1 && month <= 12 && !isNaN(year) && year >= 1900) {
        moengage.add_birthday(new Date(year, month - 1, 1));
      }
    }
    // Note: 'name' is a reserved attribute key — the SDK silently drops
    // add_user_attribute('name', ...), so the full name goes via add_first_name above.
    if (givenName) moengage.add_user_attribute('givenName', givenName);
    if (familyName) moengage.add_user_attribute('familyName', familyName);
    if (gender) moengage.add_user_attribute('gender', gender);
    if (birthMonth) moengage.add_user_attribute('birthMonth', birthMonth);
    if (birthYear) moengage.add_user_attribute('birthYear', birthYear);
    if (lookupResult?.avatar) moengage.add_user_attribute('avatar', lookupResult.avatar);
    if (nickname) moengage.add_user_attribute('nickname', nickname);
    if (interests !== undefined) {
      moengage.add_user_attribute('interests', interests.length === 0 ? 'Unknown' : interests);
    }
    if (province) moengage.add_user_attribute('province', province);
    if (address) {
      if (address.postalCode) moengage.add_user_attribute('postalCode', address.postalCode);
      if (address.region) moengage.add_user_attribute('region', address.region);
      if (address.locality) moengage.add_user_attribute('locality', address.locality);
      moengage.add_user_attribute('address', address);
    }
    for (const [key, val] of subscriptionFlags) {
      moengage.add_user_attribute(key, val);
    }

    console.log(`${LOG_PREFIX} identifyAndSyncUser done`, { userId, identifier: identifier?.type });
  } catch (error) {
    console.error(`${LOG_PREFIX} MoEngage SDK calls failed (userId still returned):`, error);
  }

  return { userId, userAttributes };
};

